import type { Composer } from 'vue-i18n'
import { until, useDocumentVisibility, useEventListener, useIntervalFn } from '@vueuse/core'

export default defineNuxtPlugin({
  name: 'back-in-stock',
  setup(nuxtApp) {
    const notifications = useBackInStock()
    const auth = useAuth()
    const route = useRoute()
    const drawers = useDrawers()
    const { locale } = nuxtApp.$i18n as Composer
    const visibility = useDocumentVisibility()
    const hasConsumers = computed(() => notifications.consumers.value > 0)
    async function refresh() {
      if (!auth.loggedIn.value || !hasConsumers.value || visibility.value !== 'visible')
        return
      try {
        await notifications.load(notifications.detailConsumers.value > 0)
      }
      catch (error) {
        console.error('Failed to refresh back in stock notifications', error)
      }
    }
    nuxtApp.hook('app:mounted', () => {
      notifications.restore()
      watch([() => auth.activeCustomer.value?.id, locale], async (_next, previous, onCleanup) => {
        let cancelled = false
        onCleanup(() => {
          cancelled = true
        })
        // The notifications page may already be loading on initial mount.
        // Clear only actual account/market transitions, so that read is shared.
        if (previous?.length) {
          notifications.closeGuest()
          notifications.clearCustomer()
        }
        if (notifications.busy.value)
          await until(notifications.busy).toBe(false)
        if (!cancelled) {
          await notifications.completePending()
          await refresh()
        }
      }, { immediate: true })
      watch(() => route.fullPath, () => {
        notifications.closeGuest()
        if (notifications.pending.value && route.path.includes('/auth/'))
          drawers.value.cart.open = false
      })
      watch([notifications.consumerRevision, notifications.detailConsumers, visibility], () => void refresh(), { immediate: true })
      useEventListener(window, 'focus', refresh)
      // Poll only displayed controls with active watches. Empty lists refresh
      // when a control is shown again or the tab regains focus.
      const { pause, resume } = useIntervalFn(() => void refresh(), 60_000, { immediate: false })
      watch(() => auth.loggedIn.value && hasConsumers.value && visibility.value === 'visible' && notifications.subscriptions.value.length > 0, (poll) => {
        if (poll)
          resume()
        else
          pause()
      }, { immediate: true })
    })
    nuxtApp.hook('auth:logout', () => {
      notifications.closeGuest()
      notifications.clearPending()
      notifications.clearCustomer()
    })
  },
})
